/**
 * VerifyNumberModal
 *
 * Verifies ownership of a user's OWN phone number (e.g. an LCC staffer's Verizon
 * number) so it can be used for outbound comms WITHOUT porting to Twilio.
 *
 * Two paths, backed by app/routers/user_phone_numbers.py:
 *   - Voice / no-port caller ID: POST /verify/caller-id then poll
 *     GET /verify/caller-id/status. Twilio calls the number and reads a code;
 *     the user enters it on their phone keypad, we poll until it's a verified
 *     outgoing caller ID.
 *   - SMS ownership: POST /verify/sms then POST /verify/sms/confirm with the
 *     6-digit code the user receives by text.
 */
import React, { useCallback, useState } from 'react';
import { Phone, MessageSquare, ShieldCheck, X, Loader2, Check } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

type Method = 'caller_id' | 'sms';
type Step = 'choose' | 'caller_id_calling' | 'sms_code' | 'done';

interface VerifyNumberModalProps {
  phoneNumberId: string;
  phoneNumber: string;
  organizationId: number;
  onClose: () => void;
  onVerified?: () => void;
}

export const VerifyNumberModal: React.FC<VerifyNumberModalProps> = ({
  phoneNumberId,
  phoneNumber,
  organizationId,
  onClose,
  onVerified,
}) => {
  const [method, setMethod] = useState<Method | null>(null);
  const [step, setStep] = useState<Step>('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState('');

  const headers = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      'X-CBO-ID': String(organizationId || ''),
    }),
    [organizationId],
  );

  const base = `/api/user-phone-numbers/${phoneNumberId}`;

  const post = async (path: string, body?: unknown) => {
    const res = await fetch(getWeaveinApiUrl(`${base}${path}`), {
      method: 'POST',
      headers: headers(),
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.detail || 'Request failed');
    return data;
  };

  // ── Verified Caller ID (voice) ──────────────────────────────────────
  const startCallerId = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await post('/verify/caller-id');
      setMethod('caller_id');
      setValidationCode(data.validation_code ?? null);
      setStep('caller_id_calling');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start verification');
    } finally {
      setBusy(false);
    }
  };

  const checkCallerId = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl(`${base}/verify/caller-id/status`), {
        headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Status check failed');
      if (data.status === 'verified') {
        setStep('done');
        onVerified?.();
      } else {
        setError('Not verified yet — answer the call and enter the code, then check again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Status check failed');
    } finally {
      setBusy(false);
    }
  };

  // ── SMS ownership ───────────────────────────────────────────────────
  const startSms = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/verify/sms');
      setMethod('sms');
      setStep('sms_code');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const confirmSms = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await post('/verify/sms/confirm', { code: smsCode.trim() });
      if (data.status === 'verified') {
        setStep('done');
        onVerified?.();
      } else {
        setError('That code was not accepted. Please try again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Confirmation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            Verify {phoneNumber}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          {step === 'choose' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Prove you own this number so you can use it without porting it to us.
              </p>
              <button
                onClick={startCallerId}
                disabled={busy}
                className="flex w-full items-start gap-3 rounded-lg border border-gray-200 p-4 text-left hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-indigo-900/20"
              >
                <Phone className="mt-0.5 h-5 w-5 text-indigo-600" />
                <span>
                  <span className="block font-medium text-gray-900 dark:text-gray-100">
                    Verify for calls (Caller ID)
                  </span>
                  <span className="block text-sm text-gray-500 dark:text-gray-400">
                    We call your number and read a code. No porting needed.
                  </span>
                </span>
              </button>
              <button
                onClick={startSms}
                disabled={busy}
                className="flex w-full items-start gap-3 rounded-lg border border-gray-200 p-4 text-left hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-indigo-900/20"
              >
                <MessageSquare className="mt-0.5 h-5 w-5 text-indigo-600" />
                <span>
                  <span className="block font-medium text-gray-900 dark:text-gray-100">
                    Verify for texts (SMS code)
                  </span>
                  <span className="block text-sm text-gray-500 dark:text-gray-400">
                    We text a 6-digit code to confirm you receive messages here.
                  </span>
                </span>
              </button>
            </div>
          )}

          {step === 'caller_id_calling' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Twilio is calling <span className="font-medium">{phoneNumber}</span>. Answer it and
                enter the code you hear on your phone keypad.
              </p>
              {validationCode && (
                <div className="rounded-lg bg-gray-50 p-4 text-center dark:bg-gray-900">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Code you should hear</div>
                  <div className="mt-1 font-mono text-2xl font-bold tracking-widest text-gray-900 dark:text-gray-100">
                    {validationCode}
                  </div>
                </div>
              )}
              <button
                onClick={checkCallerId}
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                I&apos;ve entered the code — check status
              </button>
            </div>
          )}

          {step === 'sms_code' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Enter the 6-digit code we texted to <span className="font-medium">{phoneNumber}</span>.
              </p>
              <input
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                placeholder="123456"
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-center font-mono text-xl tracking-widest focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <button
                onClick={confirmSms}
                disabled={busy || smsCode.length < 4}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Confirm code
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                <span className="font-medium">{phoneNumber}</span> is verified
                {method === 'caller_id' ? ' as an outbound caller ID.' : ' for messaging.'}
              </p>
              <button
                onClick={onClose}
                className="w-full rounded-md bg-gray-100 px-4 py-2 font-medium text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyNumberModal;
